import { useState } from 'react';
import PropTypes from 'prop-types';

import { legacyTheme, styled } from '@prose-ui/legacy';
import Image from 'next/image';

import Typography from 'Components/Typography';
import CartAccessoriesMobileFooter from '../Components/CartAccessoriesMobileFooter';

import close from 'assets/images/icon_close.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import CartOrderSummaryV2 from './CartOrderSummaryV2';

const CheckoutTicketContainer = styled.div`
  z-index: 9999;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  max-width: 400px;

  background-color: ${legacyTheme.palette.common.white.light};

  ${legacyTheme.breakpoints.up('sm')} {
    z-index: 0;

    padding: ${legacyTheme.props.minimumBlockPadding};
    padding-top: 90px;
    padding-left: 0;

    background-color: transparent;
  }
`;

const TicketHeader = styled.div`
  position: sticky;
  padding-top: 30px;
`;

const TicketCloseButton = styled.button`
  position: absolute;
  top: 30px;
  right: 30px;
  border: none;
  background-color: transparent;
  font: inherit;
  cursor: pointer;
  ${legacyTheme.breakpoints.up('sm')} {
    display: none;
  }
`;

const TicketContent = styled.div`
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  padding: 0 32px;
  margin-bottom: auto;
  height: 100%;

  box-shadow: inset 0 -10px 10px -10px rgba(0, 0, 0, 0.06);
  /**
   * Approximately 1 item in the list,
   * In case the user disabled the scrollbar showing by default we are afraid they will miss that
   * they are able to scroll. So we re-implement the scrollbar completely in a similar style.
   */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    &:vertical {
      width: 11px;
    }
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 2px solid white; /* should match background, can't be transparent */
    background-color: rgba(0, 0, 0, 0.3);
  }
  &::-webkit-scrollbar-track {
    background-color: #fff;
    border-radius: 8px;
  }
  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 0;
    box-shadow: unset;
  }
`;

const TicketContainer = styled.div`
  position: fixed;
  z-index: 999999;
  visibility: ${({ showCart }) => (showCart ? 'visible' : 'hidden')};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  min-height: 0; // Firefox
  height: 100%;
  width: 100%;

  background-color: ${legacyTheme.palette.common.white.light};

  transform: translate3d(0, ${({ showCart }) => (showCart ? '0' : '100%')}, 0);
  transition: all 360ms cubic-bezier(0.455, 0.03, 0.515, 0.955);

  ${legacyTheme.breakpoints.up('sm')} {
    position: static;
    z-index: 0;
    visibility: visible;

    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    margin: 0 auto;
    height: unset;

    transform: translate3d(0, 0, 0);
    box-shadow: 2px 2px 3px 0 ${legacyTheme.palette.common.grey.light};
  }
`;

const TicketBorder = styled.hr`
  margin: 16px 0;
  border: none;
  border-top: 1px solid ${legacyTheme.palette.common.grey.light};

  ${legacyTheme.breakpoints.up('sm')} {
    margin: 24px 32px;
  }
`;

const CheckoutTicketCartAccessories = ({ ctaButtonProps, isCheckout }) => {
  const { isMobile } = useResponsiveVariant();
  const [showCart, setShowCart] = useState(false);

  const hideOrderSummary = isCheckout && isMobile;

  return (
    <CheckoutTicketContainer data-testid="checkout-ticket-v2">
      {!hideOrderSummary && (
        <TicketContainer showCart={showCart}>
          <TicketHeader>
            <Typography align="center" bold variant="mono1">
              Order Summary
            </Typography>
            <TicketCloseButton
              data-click="order-summary-close"
              data-from="cart"
              onClick={() => setShowCart(false)}
              type="button"
            >
              <Image alt="Close icon - vector" height={12} src={close} width={12} />
            </TicketCloseButton>
          </TicketHeader>
          <TicketBorder />
          <TicketContent>
            <CartOrderSummaryV2 ctaButtonProps={ctaButtonProps} isCheckout={isCheckout} />
          </TicketContent>
        </TicketContainer>
      )}

      {!isCheckout && isMobile && (
        <CartAccessoriesMobileFooter
          ctaButtonProps={ctaButtonProps}
          isCheckout={isCheckout}
          onToggleCart={() => setShowCart(true)}
        />
      )}
    </CheckoutTicketContainer>
  );
};

CheckoutTicketCartAccessories.propTypes = {
  ctaButtonProps: PropTypes.shape({
    text: PropTypes.string,
    disabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    onClick: PropTypes.func,
    type: PropTypes.string,
    variant: PropTypes.string,
  }),
  isCheckout: PropTypes.bool,
};

CheckoutTicketCartAccessories.defaultProps = {
  ctaButtonProps: {},
  isCheckout: false,
};

export default CheckoutTicketCartAccessories;
